import { z } from 'zod';
import { AuthStatus, useAuth } from 'auth';
import Form from 'components/Form/Form';
import InputField from 'components/Form/InputField';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/icon';
import useDocumentTitle from 'hooks/useDocumentTitle';
import ProductImage from 'my-account/images/product-01.jpg';
import { Link } from 'react-router-dom';

const TwoFactorSchema = z.object({
  code: z.string().nonempty(),
});

function TwoFactorForm() {
  const auth = useAuth();
  const isLoggingIn =
    auth.status !== AuthStatus.TwoFactorRequired &&
    (auth.status === AuthStatus.LoggingIn || auth.loginMutationState.isLoading);

  return (
    <Form
      schema={TwoFactorSchema}
      initialValues={{ code: '' }}
      onSubmit={async (data) => {
        try {
          await auth.verifyTwoFactor(data.code);
        } catch (error) {
          console.error('Two-Factor Authentication verification failed:', error);
          throw error;
        }
      }}
      className="c-form-group c-form-group--sm c-form-group--ver u-ml-auto u-mr-auto"
      hasFloatingLabels
    >
      <div className="o-row o-row--no-gutters c-form-group__dividers u-flex-grow">
        <div className="o-col-12">
          <InputField
            name="code"
            type="text"
            label="Two-Factor Authentication Code:"
            className="c-form-element--high c-form-element--style-line"
            disabled={isLoggingIn}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </div>
      </div>
      <div className="c-form-footer">
        <SubmitButton isLoading={isLoggingIn} isFull>
          Log in
        </SubmitButton>
      </div>
    </Form>
  );
}

export default function TwoFactorPage() {
  useDocumentTitle('Two-Factor Authentication');

  const auth = useAuth();
  return (
    <section
      className="c-block c-block--spacing-t-extra-small c-block--spacing-t-small@md c-block--spacing-b-extra-small c-block--spacing-b-small@md c-block--bg-light2 c-block--hero c-block--height-semifull@xs c-block--bg-image"
      style={{ backgroundImage: `url(${ProductImage})` }}
    >
      <div className="o-container-fluid">
        <div className="o-row u-flex-grow u-items-center">
          <div className="o-col-12">
            <div className="c-block__header c-block__header--xl c-block__header--large u-items-center u-text-center">
              <h1 className="c-title--extra-large">Two-Factor Authentication</h1>
              <div className="c-hero__desc">
                <p>Please check your email to retrieve the authentication code that was sent to your inbox.</p>
              </div>
            </div>
            <TwoFactorForm />
            <div className="u-justify-center u-text-center">
              <p className="u-mb-0">
                <Link className="c-link-cta-basic" to="#" onClick={() => auth.resend2FA()}>
                  <span>Resend code</span>
                </Link>
              </p>
              <div className="c-form-group c-form-group--sm u-mx-auto u-mb-spacer-base">
                <Link to="/auth/salesforce" className="c-button c-button--dark c-button--full">
                  <span>Salesforce log in</span>
                </Link>
              </div>
            </div>
            <div className="c-info c-info--sm u-mx-auto">
              <div className="c-info__header">
                <div className="c-info__icon u-text-secondary">
                  <Icon name="info" className="o-svg-icon" />
                </div>
                <p>
                  First time here?
                  <Link
                    to="/auth/request-account"
                    className="c-link-cta-basic c-link-cta--small u-mb-0 u-ml-spacer-base-small@sm"
                  >
                    <span>Request an account</span>
                    <Icon name="arrow" className="o-svg-icon o-svg-right" />
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="c-block__pattern c-block__pattern--light3 c-block__overlay--opacity-40">
        <Icon name="pattern" className="o-svg-icon" />
      </div>
      <div className="c-block__overlay c-block__overlay--light2 c-block__overlay--opacity-85"></div>
    </section>
  );
}
